import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  FileText, 
  Calendar, 
  Target, 
  TrendingUp, 
  Youtube,
  BookOpen,
  Send,
  Eye,
  Download,
  BarChart3,
  Globe
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

interface ContentItem {
  id: string;
  type: 'blog' | 'whitepaper' | 'video';
  title: {
    de: string;
    en: string;
    fr: string;
    it: string;
  };
  status: 'draft' | 'published' | 'scheduled';
  publishDate: string;
  targetKeywords: string[];
  views: number;
  engagement: number;
}

const ContentAuthorityDomination: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { toast } = useToast();
  const [newContent, setNewContent] = useState({
    type: 'blog',
    title: '',
    content: '',
    keywords: '',
    targetLanguages: [currentLanguage],
    publishDate: '',
    zapierWebhook: ''
  });

  const translations = {
    de: {
      title: 'Content & Authority Domination',
      subtitle: 'Wöchentliche Blogs, Whitepapers & YouTube Content in 4 Sprachen',
      blogFactory: 'Blog Factory',
      weeklyBlogs: 'Wöchentliche Blogs (4 Sprachen)',
      whitepapers: 'Quartalsweise Whitepapers',
      videoContent: 'YouTube & Shorts',
      contentCalendar: 'Content Kalender',
      createContent: 'Content Erstellen',
      scheduleContent: 'Content Planen',
      publishContent: 'Veröffentlichen',
      contentType: 'Content-Typ',
      contentTitle: 'Titel',
      contentBody: 'Inhalt',
      targetKeywords: 'Ziel-Keywords',
      publishDate: 'Veröffentlichungsdatum',
      targetLanguages: 'Zielsprachen',
      zapierWebhook: 'Zapier Webhook für Automatisierung',
      stats: {
        published: 'Veröffentlicht',
        views: 'Aufrufe',
        engagement: 'Engagement',
        rankings: 'Top Rankings'
      },
      suggestedTopics: {
        blog: [
          'Beste AI Marketing Tools für Schweizer KMU',
          'Wie Pharma in Basel AR für Training nutzt',
          'SEO Preise in der Schweiz 2025 erklärt',
          'Digitale Transformation für Schweizer Unternehmen'
        ],
        whitepaper: [
          'AI in der Schweizer Pharmaindustrie',
          'Multilingual SEO Guide für Schweizer KMU',
          'Swiss Digital Compliance (revFADP)',
          'Zukunft des Marketings in der Schweiz'
        ],
        video: [
          'Calculator Demo in 4 Sprachen',
          'AI Influencer Showcase',
          'AR/VR Previews für Schweizer Marken',
          'Behind the Scenes: Swiss AI Agency'
        ]
      }
    },
    en: {
      title: 'Content & Authority Domination',
      subtitle: 'Weekly Blogs, Whitepapers & YouTube Content in 4 Languages',
      blogFactory: 'Blog Factory',
      weeklyBlogs: 'Weekly Blogs (4 Languages)',
      whitepapers: 'Quarterly Whitepapers',
      videoContent: 'YouTube & Shorts',
      contentCalendar: 'Content Calendar',
      createContent: 'Create Content',
      scheduleContent: 'Schedule Content',
      publishContent: 'Publish',
      contentType: 'Content Type',
      contentTitle: 'Title',
      contentBody: 'Content',
      targetKeywords: 'Target Keywords',
      publishDate: 'Publish Date',
      targetLanguages: 'Target Languages',
      zapierWebhook: 'Zapier Webhook for Automation',
      stats: {
        published: 'Published',
        views: 'Views',
        engagement: 'Engagement',
        rankings: 'Top Rankings'
      },
      suggestedTopics: {
        blog: [
          'Best AI Marketing Tools for Swiss SMEs',
          'How Pharma in Basel Uses AR for Training',
          'SEO Pricing in Switzerland 2025 Explained',
          'Digital Transformation for Swiss Companies'
        ],
        whitepaper: [
          'AI in Swiss Pharmaceutical Industry',
          'Multilingual SEO Guide for Swiss SMEs',
          'Swiss Digital Compliance (revFADP)',
          'Future of Marketing in Switzerland'
        ],
        video: [
          'Calculator Demo in 4 Languages',
          'AI Influencer Showcase',
          'AR/VR Previews for Swiss Brands',
          'Behind the Scenes: Swiss AI Agency'
        ]
      }
    },
    fr: {
      title: 'Domination Contenu & Autorité',
      subtitle: 'Blogs Hebdomadaires, Livres Blancs & Contenu YouTube en 4 Langues',
      blogFactory: 'Usine à Blog',
      weeklyBlogs: 'Blogs Hebdomadaires (4 Langues)',
      whitepapers: 'Livres Blancs Trimestriels',
      videoContent: 'YouTube & Shorts',
      contentCalendar: 'Calendrier de Contenu',
      createContent: 'Créer du Contenu',
      scheduleContent: 'Planifier le Contenu',
      publishContent: 'Publier',
      contentType: 'Type de Contenu',
      contentTitle: 'Titre',
      contentBody: 'Contenu',
      targetKeywords: 'Mots-clés Cibles',
      publishDate: 'Date de Publication',
      targetLanguages: 'Langues Cibles',
      zapierWebhook: 'Webhook Zapier pour Automatisation',
      stats: {
        published: 'Publié',
        views: 'Vues',
        engagement: 'Engagement',
        rankings: 'Top Rankings'
      },
      suggestedTopics: {
        blog: [
          'Meilleurs Outils Marketing IA pour PME Suisses',
          'Comment Pharma à Bâle Utilise AR pour Formation',
          'Prix SEO en Suisse 2025 Expliqués',
          'Transformation Digitale pour Entreprises Suisses'
        ],
        whitepaper: [
          'IA dans l\'Industrie Pharmaceutique Suisse',
          'Guide SEO Multilingue pour PME Suisses',
          'Conformité Digitale Suisse (revFADP)',
          'Avenir du Marketing en Suisse'
        ],
        video: [
          'Démo Calculateur en 4 Langues',
          'Showcase Influenceur IA',
          'Aperçus AR/VR pour Marques Suisses',
          'Coulisses: Agence IA Suisse'
        ]
      }
    },
    it: {
      title: 'Dominazione Contenuti & Autorità',
      subtitle: 'Blog Settimanali, Whitepaper & Contenuto YouTube in 4 Lingue',
      blogFactory: 'Fabbrica di Blog',
      weeklyBlogs: 'Blog Settimanali (4 Lingue)',
      whitepapers: 'Whitepaper Trimestrali',
      videoContent: 'YouTube & Shorts',
      contentCalendar: 'Calendario Contenuti',
      createContent: 'Crea Contenuto',
      scheduleContent: 'Pianifica Contenuto',
      publishContent: 'Pubblica',
      contentType: 'Tipo di Contenuto',
      contentTitle: 'Titolo',
      contentBody: 'Contenuto',
      targetKeywords: 'Parole Chiave Target',
      publishDate: 'Data di Pubblicazione',
      targetLanguages: 'Lingue Target',
      zapierWebhook: 'Webhook Zapier per Automazione',
      stats: {
        published: 'Pubblicato',
        views: 'Visualizzazioni',
        engagement: 'Engagement',
        rankings: 'Top Rankings'
      },
      suggestedTopics: {
        blog: [
          'Migliori Strumenti Marketing AI per PMI Svizzere',
          'Come Pharma a Basilea Usa AR per Training',
          'Prezzi SEO in Svizzera 2025 Spiegati',
          'Trasformazione Digitale per Aziende Svizzere'
        ],
        whitepaper: [
          'AI nell\'Industria Farmaceutica Svizzera',
          'Guida SEO Multilingue per PMI Svizzere',
          'Conformità Digitale Svizzera (revFADP)',
          'Futuro del Marketing in Svizzera'
        ],
        video: [
          'Demo Calcolatore in 4 Lingue',
          'Showcase Influencer AI',
          'Anteprime AR/VR per Brand Svizzeri',
          'Dietro le Quinte: Agenzia AI Svizzera'
        ]
      }
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const contentItems: ContentItem[] = [
    {
      id: '1',
      type: 'blog',
      title: {
        de: 'AI Marketing Tools für Schweizer KMU 2025',
        en: 'AI Marketing Tools for Swiss SMEs 2025',
        fr: 'Outils Marketing IA pour PME Suisses 2025',
        it: 'Strumenti Marketing AI per PMI Svizzere 2025'
      },
      status: 'published',
      publishDate: '2024-12-15',
      targetKeywords: ['AI Marketing', 'Swiss SME', 'Digital Tools'],
      views: 2847,
      engagement: 23
    },
    {
      id: '2',
      type: 'whitepaper',
      title: {
        de: 'Schweizer Pharma & AI: Compliance Guide',
        en: 'Swiss Pharma & AI: Compliance Guide',
        fr: 'Pharma Suisse & IA: Guide Conformité',
        it: 'Pharma Svizzero & AI: Guida Conformità'
      },
      status: 'scheduled',
      publishDate: '2024-12-20',
      targetKeywords: ['Swiss Pharma', 'AI Compliance', 'revFADP'],
      views: 0,
      engagement: 0
    },
    {
      id: '3',
      type: 'video',
      title: {
        de: 'Calculator Demo: 4 Sprachen in Aktion',
        en: 'Calculator Demo: 4 Languages in Action',
        fr: 'Démo Calculateur: 4 Langues en Action',
        it: 'Demo Calcolatore: 4 Lingue in Azione'
      },
      status: 'draft',
      publishDate: '2024-12-22',
      targetKeywords: ['Calculator Demo', 'Multilingual', 'Swiss Tools'],
      views: 0,
      engagement: 0
    }
  ];

  const handleCreateContent = async () => {
    if (!newContent.title || !newContent.content) {
      toast({
        title: "Error",
        description: "Please fill in title and content",
        variant: "destructive"
      });
      return;
    }

    if (newContent.zapierWebhook) {
      try {
        await fetch(newContent.zapierWebhook, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          mode: "no-cors",
          body: JSON.stringify({
            action: 'create_content',
            content: newContent,
            timestamp: new Date().toISOString()
          }),
        });
      } catch (error) {
        console.error('Zapier webhook failed:', error);
      }
    }

    toast({
      title: "Content Created",
      description: `${newContent.type} created and scheduled for publication`
    });

    // Reset form
    setNewContent({
      type: 'blog',
      title: '',
      content: '',
      keywords: '',
      targetLanguages: [currentLanguage],
      publishDate: '',
      zapierWebhook: ''
    });
  };

  const contentStats = {
    published: '247',
    views: '1.2M',
    engagement: '8.4%',
    rankings: '156'
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl text-muted-foreground">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Stats Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
        >
          {Object.entries(contentStats).map(([key, value], index) => (
            <Card key={key} className="neo-card">
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-primary mb-2">{value}</div>
                <div className="text-sm text-muted-foreground">
                  {t.stats[key as keyof typeof t.stats]}
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Content Creation Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="neo-card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="w-6 h-6 text-primary" />
                  <span>{t.createContent}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t.contentType}</label>
                  <Select 
                    value={newContent.type} 
                    onValueChange={(value) => setNewContent(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger className="neo-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blog">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4" />
                          <span>Blog Post</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="whitepaper">
                        <div className="flex items-center space-x-2">
                          <BookOpen className="w-4 h-4" />
                          <span>Whitepaper</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="video">
                        <div className="flex items-center space-x-2">
                          <Youtube className="w-4 h-4" />
                          <span>Video Content</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t.contentTitle}</label>
                  <Input
                    value={newContent.title}
                    onChange={(e) => setNewContent(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter content title..."
                    className="neo-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t.contentBody}</label>
                  <Textarea
                    value={newContent.content}
                    onChange={(e) => setNewContent(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter content body..."
                    className="neo-input min-h-[120px]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t.targetKeywords}</label>
                  <Input
                    value={newContent.keywords}
                    onChange={(e) => setNewContent(prev => ({ ...prev, keywords: e.target.value }))}
                    placeholder="AI Marketing, Swiss SME, Digital Tools"
                    className="neo-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t.publishDate}</label>
                  <Input
                    type="datetime-local"
                    value={newContent.publishDate}
                    onChange={(e) => setNewContent(prev => ({ ...prev, publishDate: e.target.value }))}
                    className="neo-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t.zapierWebhook}</label>
                  <Input
                    value={newContent.zapierWebhook}
                    onChange={(e) => setNewContent(prev => ({ ...prev, zapierWebhook: e.target.value }))}
                    placeholder="https://hooks.zapier.com/hooks/catch/..."
                    className="neo-input"
                  />
                </div>

                <Button
                  onClick={handleCreateContent}
                  className="neo-button w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {t.createContent}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Content Calendar & Suggestions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Recent Content */}
            <Card className="neo-card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-6 h-6 text-primary" />
                  <span>{t.contentCalendar}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contentItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">
                          {item.title[currentLanguage as keyof typeof item.title]}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge 
                            variant={item.status === 'published' ? 'default' : item.status === 'scheduled' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {item.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{item.publishDate}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <Eye className="w-3 h-3" />
                        <span>{item.views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Suggested Topics */}
            <Card className="neo-card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Target className="w-6 h-6 text-primary" />
                  <span>Suggested Topics</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(t.suggestedTopics).map(([type, topics]) => (
                    <div key={type}>
                      <h4 className="font-semibold text-sm mb-2 capitalize">{type}</h4>
                      <div className="space-y-1">
                        {topics.slice(0, 3).map((topic, index) => (
                          <button
                            key={index}
                            onClick={() => setNewContent(prev => ({ ...prev, title: topic, type: type as any }))}
                            className="text-xs text-left w-full p-2 bg-muted/30 hover:bg-muted/50 rounded transition-colors"
                          >
                            {topic}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Performance Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <Card className="neo-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                <span>Content Performance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Top Performing Content</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>AI Marketing Tools Guide</span>
                      <span className="text-green-500">+340% views</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Swiss Pharma Compliance</span>
                      <span className="text-green-500">+280% engagement</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Multilingual SEO Tips</span>
                      <span className="text-green-500">+220% shares</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Language Performance</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>🇩🇪 German</span>
                      <span>45% traffic</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>🇬🇧 English</span>
                      <span>25% traffic</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>🇫🇷 French</span>
                      <span>20% traffic</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>🇮🇹 Italian</span>
                      <span>10% traffic</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Next Milestones</h4>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Q1 Whitepaper
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Youtube className="w-4 h-4 mr-2" />
                      Video Series Launch
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Globe className="w-4 h-4 mr-2" />
                      EU Expansion Content
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ContentAuthorityDomination;